import { Link } from '@tanstack/react-router';
import { useTranslation } from '@/i18n';
import { GameCard } from './GameCard';
import { TopScores } from './TopScores';

export function HomePage() {
  const { t } = useTranslation();

  const GAMES = [
    {
      id: 'cabinet-transformer',
      title: t.home.cabinetTitle,
      subtitle: t.home.cabinetSubtitle,
      description: t.home.cabinetDesc,
      path: '/games/cabinet-transformer',
      color: 'green' as const,
      icon: '🎯',
    },
    {
      id: 'automation-drop',
      title: t.home.automationTitle,
      subtitle: t.home.automationSubtitle,
      description: t.home.automationDesc,
      path: '/games/automation-drop',
      color: 'blue' as const,
      icon: '🔧',
    },
  ];

  return (
    <div className="animate-fade-in">
      {/* Hero Section - responsive padding and text sizes */}
      <section className="text-center py-8 sm:py-12 md:py-20">
        <div className="inline-flex items-center gap-2 bg-ukonnx-green/10 text-ukonnx-green 
                      px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6">
          <span className="animate-pulse">●</span>
          {t.home.poweredBy}
        </div>
        
        <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-text-primary mb-3 sm:mb-4 px-2">
          {t.home.hero}
          <span className="text-ukonnx-green">{t.home.heroHighlight}</span>
        </h1>
        
        <p className="text-text-secondary text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-6 sm:mb-8 px-4">
          {t.home.heroSubtitle}
        </p>

        <div className="flex flex-wrap gap-3 sm:gap-4 justify-center px-4">
          <Link to="/leaderboard" className="btn-secondary min-h-[44px] px-6">
            {t.home.viewLeaderboard}
          </Link>
        </div>
      </section>

      {/* Games Grid - responsive grid */}
      <section className="py-8 sm:py-12">
        <h2 className="text-xl sm:text-2xl font-bold text-text-primary mb-6 sm:mb-8 text-center">
          {t.home.chooseGame}
        </h2>
        
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
          {GAMES.map((game) => (
            <GameCard key={game.id} game={game} playNowLabel={t.home.playNow} />
          ))}
        </div>
      </section>

      {/* Top Scores Preview */}
      <section className="py-8 sm:py-12">
        <div className="flex items-center justify-between mb-6 sm:mb-8 max-w-2xl mx-auto px-2">
          <h2 className="text-xl sm:text-2xl font-bold text-text-primary">
            {t.home.topPlayers}
          </h2>
          <Link to="/leaderboard" className="btn-ghost text-ukonnx-green min-h-[44px]">
            {t.home.viewAll}
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-2xl mx-auto">
          {GAMES.map((game) => (
            <TopScores key={game.id} gameId={game.id} gameName={game.title} />
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-8 sm:py-12">
        <h2 className="text-xl sm:text-2xl font-bold text-text-primary mb-6 sm:mb-8 text-center">
          {t.home.howItWorks}
        </h2>
        
        <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto">
          <StepCard number={1} title={t.home.step1Title} description={t.home.step1Desc} />
          <StepCard number={2} title={t.home.step2Title} description={t.home.step2Desc} />
          <StepCard number={3} title={t.home.step3Title} description={t.home.step3Desc} />
        </div>
      </section>
    </div>
  );
}

function StepCard({ number, title, description }: { 
  number: number; 
  title: string; 
  description: string 
}) {
  return (
    <div className="card p-4 sm:p-6 text-center">
      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-ukonnx-green/20 text-ukonnx-green rounded-full 
                    flex items-center justify-center text-lg sm:text-xl font-bold mx-auto mb-3 sm:mb-4">
        {number}
      </div>
      <h3 className="font-semibold text-text-primary mb-2 text-sm sm:text-base">{title}</h3>
      <p className="text-text-secondary text-xs sm:text-sm">{description}</p>
    </div>
  );
}
